/* ==========================================================================
   The Vault – enhancements (2026-10-30)
   Loaded with `defer` after assets/theme.js. Vanilla JS, no dependencies.
   Every feature is optional: if this file fails to load, the theme works as before.
     7.  Cart page: quantity changes update the cart (and the free-shipping bar) without a reload.
     9.  Touch foil sweep on product cards (desktop tilt/sheen stays in theme.js).
     10. Add-to-cart feedback: cart icon bounce + screen-reader announcement.
   Settings come from window.vaultSettings (layout/theme.liquid).
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.vaultSettings || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function announce(text) {
    var el = document.getElementById('VaultAnnouncer');
    if (!el || !text) return;
    el.textContent = '';
    // A short delay makes screen readers treat the same text as a new message.
    setTimeout(function () { el.textContent = text; }, 60);
  }

  /* ------------------------------------------------------------------------
     7. Cart page: live quantity updates
     theme.js submits the whole form (page reload) on quantity change. This
     handler runs first (capture phase), updates the line through the Cart API
     and re-renders the cart section with the Section Rendering API, so totals
     and the free-shipping bar update in place. Any failure falls back to the
     original form submit.
     ------------------------------------------------------------------------ */
  function initCartPage() {
    var form = document.querySelector('[data-cart-form]');
    if (!form || !window.fetch) return;
    var sectionEl = form.closest('.shopify-section');
    if (!sectionEl || !sectionEl.id) return;
    var sectionId = sectionEl.id.replace(/^shopify-section-/, '');
    var timer = null;

    function fallback(currentForm) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'update';
      hidden.value = '1';
      currentForm.appendChild(hidden);
      currentForm.submit();
    }

    document.addEventListener('change', function (e) {
      var input = e.target && e.target.closest ? e.target.closest('[data-cart-form] [data-cart-qty]') : null;
      if (!input) return;
      e.stopPropagation();
      var currentForm = input.closest('[data-cart-form]');
      var key = (input.id || '').replace(/^Qty-/, '');
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      var max = input.max ? parseInt(input.max, 10) : Infinity;
      if (qty > max) qty = max;
      clearTimeout(timer);
      timer = setTimeout(function () {
        currentForm.setAttribute('aria-busy', 'true');
        fetch(window.theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty, sections: sectionId, sections_url: window.location.pathname })
        })
          .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
          .then(function (cart) {
            var html = cart.sections && cart.sections[sectionId];
            if (!html) throw new Error('no section');
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.getElementById('shopify-section-' + sectionId) || doc.body.firstElementChild;
            var focusId = document.activeElement && document.activeElement.id;
            sectionEl.innerHTML = fresh ? fresh.innerHTML : html;
            if (focusId) {
              var again = document.getElementById(focusId);
              if (again) again.focus();
            }
            document.querySelectorAll('[data-cart-count]').forEach(function (el) {
              el.textContent = cart.item_count;
              el.hidden = cart.item_count === 0;
            });
            var msg = sectionEl.querySelector('[data-free-shipping] .vx-ship__msg');
            if (msg) announce(msg.textContent.trim());
          })
          .catch(function () { fallback(currentForm); });
      }, 400);
    }, true);
  }

  /* ------------------------------------------------------------------------
     9. Touch foil sweep
     On touch/pen pointers, a card tilts slightly toward the finger and a
     rainbow foil band sweeps across it. Listeners are passive and never call
     preventDefault, so the tap/link is not blocked or delayed.
     ------------------------------------------------------------------------ */
  function initTouchFoil() {
    if (!cfg.holoTouch || reduceMotion.matches) return;
    document.documentElement.classList.add('vx-touch-foil');
    var max = 6;
    document.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      var card = e.target && e.target.closest ? e.target.closest('.card') : null;
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var px = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      var py = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
      card.style.setProperty('--tilt-y', ((px - 0.5) * max * 2).toFixed(2) + 'deg');
      card.style.setProperty('--tilt-x', ((0.5 - py) * max * 2).toFixed(2) + 'deg');
      card.style.setProperty('--sheen-x', (px * 100).toFixed(1) + '%');
      card.style.setProperty('--sheen-y', (py * 100).toFixed(1) + '%');
      card.classList.remove('is-foil');
      // Force a reflow so the sweep restarts on repeated taps.
      void card.offsetWidth;
      card.classList.add('is-foil');
      clearTimeout(card.__vxFoil);
      card.__vxFoil = setTimeout(function () {
        card.classList.remove('is-foil');
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      }, 750);
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     10. Add-to-cart feedback
     theme.js fires `cart:added` after a successful /cart/add.js and updates
     the badge count. Here the header cart icon bounces once and the result is
     announced in the polite live region (#VaultAnnouncer).
     ------------------------------------------------------------------------ */
  function initCartFeedback() {
    document.addEventListener('cart:added', function (e) {
      var item = (e && e.detail) || {};
      var title = item.product_title || item.title || '';
      if (cfg.cartBounce && !reduceMotion.matches) {
        document.querySelectorAll('[data-cart-link]').forEach(function (link) {
          link.classList.remove('vx-bump');
          void link.offsetWidth;
          link.classList.add('vx-bump');
        });
      }
      fetch(window.theme.routes.cartJs, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var s = cfg.strings || {};
          var count = cart.item_count;
          var countText = count === 1 ? (s.cartOne || '') : (s.cartOther || '').replace('[n]', count);
          announce([title, s.added, countText].filter(Boolean).join(' – '));
        })
        .catch(function () { announce([title, (cfg.strings || {}).added].filter(Boolean).join(' – ')); });
    });
    document.addEventListener('animationend', function (e) {
      if (e.animationName === 'vx-bump' && e.target.classList) e.target.classList.remove('vx-bump');
    });
  }

  function init() {
    initCartPage();
    initTouchFoil();
    initCartFeedback();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
